import { useEffect, useState } from 'react'
import { usePendingInvitations } from '@sportninja/common/features/invitation/hooks/usePendingInvitations'
import type { MyInvitation } from '@sportninja/common/services/invitation/types'
import { useSelector } from 'src/hooks/useRedux'
import {
  forgetPendingInvitesPopupShown,
  hasShownPendingInvitesPopup,
  markPendingInvitesPopupShown,
  shouldOpenPendingInvitesPopup,
} from '../utils/pendingInvitesPopupGate'

interface UsePendingInvitationsPopupArgs {
  userId: string | null | undefined
  /** Onboarding has had its say (`useOnboardingTutorial().settled`). */
  onboardingSettled: boolean
  /** Another Home modal is open: onboarding, the post composer, the timezone check. */
  otherModalOpen: boolean
}

export interface UsePendingInvitationsPopupResult {
  isOpen: boolean
  /** What it lists: the pending invitations, newest first. */
  invitations: MyInvitation[]
  /** Not Now, the close button, or leaving for an invitation or the bell. */
  close: () => void
}

/**
 * SN-7849 (C) — web Home's popup listing the invitations still waiting.
 *
 * It opens once per sign-in, from invitations fetched since Home mounted (a
 * cached list can predate the last accept), never on a framed page and never
 * over another Home modal: it waits for onboarding to settle and for any other
 * modal to close. A modal that opens over it sends it back to waiting without
 * using up its once.
 */
export const usePendingInvitationsPopup = ({
  userId,
  onboardingSettled,
  otherModalOpen,
}: UsePendingInvitationsPopupArgs): UsePendingInvitationsPopupResult => {
  'use memo'

  const inFrame = useSelector(
    (state: { auth: { inFrame: boolean } }) => state.auth.inFrame
  )
  const { pendingInvitations, isFresh } = usePendingInvitations(userId)
  const [isOpen, setIsOpen] = useState(false)
  const pendingCount = pendingInvitations.length

  useEffect(() => {
    if (isOpen) {
      if (otherModalOpen || inFrame || !userId) {
        setIsOpen(false)
        forgetPendingInvitesPopupShown(userId)
      } else if (isFresh && pendingCount === 0) {
        // Everything it listed was accepted elsewhere meanwhile.
        setIsOpen(false)
      }
      return
    }

    const opens = shouldOpenPendingInvitesPopup({
      userId,
      inFrame,
      hasFreshPendingInvitations: isFresh && pendingCount > 0,
      onboardingSettled,
      otherModalOpen,
      alreadyShown: hasShownPendingInvitesPopup(userId),
    })
    if (opens && userId) {
      markPendingInvitesPopupShown(userId)
      setIsOpen(true)
    }
  }, [
    isOpen,
    userId,
    inFrame,
    isFresh,
    pendingCount,
    onboardingSettled,
    otherModalOpen,
  ])

  return {
    isOpen,
    invitations: pendingInvitations,
    close: () => setIsOpen(false),
  }
}
